import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { Client, AdAccount } from "../lib/types";
import { SERVICE_OPTIONS, KNOWN_AD_ACCOUNTS, CURRENCY_OPTIONS } from "../lib/constants";
import {
  PageHeader,
  Loading,
  ErrorNote,
  EmptyState,
  Modal,
  Spinner,
  TableScroll,
} from "../components/ui";
import { IconPlus, IconEdit, IconArchive, IconTrash, IconUsers } from "../components/icons";

export function Clients() {
  const qc = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<Client | "new" | null>(null);
  const [inviteFor, setInviteFor] = useState<Client | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["clients", showArchived],
    queryFn: () => api.get<{ clients: Client[] }>(`/api/clients?archived=${showArchived}`),
  });

  const archive = useMutation({
    mutationFn: (c: Client) =>
      api.post(`/api/clients/${c.id}/${c.archived_at ? "restore" : "archive"}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["clients"] }),
  });

  const hardDelete = useMutation({
    mutationFn: (c: Client) => api.del(`/api/clients/${c.id}?confirm=true`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["clients"] }),
  });

  // Row/card action buttons — shared by the desktop table and the mobile cards.
  const clientActions = (c: Client) => (
    <div className="flex shrink-0 items-center gap-1">
      <button className="btn-ghost px-2 py-1" title="Manage logins" onClick={() => setInviteFor(c)}>
        <IconUsers width={15} height={15} />
      </button>
      <button className="btn-ghost px-2 py-1" title="Edit" onClick={() => setEditing(c)}>
        <IconEdit width={15} height={15} />
      </button>
      <button
        className="btn-ghost px-2 py-1"
        title={c.archived_at ? "Restore" : "Archive"}
        onClick={() => archive.mutate(c)}
      >
        <IconArchive width={15} height={15} />
      </button>
      <button
        className="btn-danger px-2 py-1"
        title="Delete permanently"
        onClick={() => {
          if (
            confirm(
              `Permanently delete "${c.name}" and ALL its revenue history? This cannot be undone. Consider archiving instead.`
            )
          )
            hardDelete.mutate(c);
        }}
      >
        <IconTrash width={15} height={15} />
      </button>
    </div>
  );

  return (
    <div>
      <PageHeader
        title="Clients"
        subtitle="Create and manage client records, ad accounts, and logins."
        actions={
          <>
            <label className="flex items-center gap-2 text-xs text-muted">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show archived
            </label>
            <button className="btn-primary" onClick={() => setEditing("new")}>
              <IconPlus width={16} height={16} /> New client
            </button>
          </>
        }
      />

      {isLoading ? (
        <Loading />
      ) : error ? (
        <ErrorNote message={(error as Error).message} />
      ) : (data?.clients.length ?? 0) === 0 ? (
        <EmptyState title="No clients yet" hint="Create your first client to begin." />
      ) : (
        <>
          {/* Mobile: stacked cards. */}
          <div className="space-y-2 md:hidden">
            {data!.clients.map((c) => (
              <div key={c.id} className="card p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/clients/${c.id}`}
                        className="font-medium text-ink hover:text-brand"
                      >
                        {c.name}
                      </Link>
                      {c.archived_at && (
                        <span className="rounded bg-surface-3 px-1.5 py-0.5 text-[10px] text-muted">
                          archived
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 break-all tnum text-xs text-ink-secondary">
                      {c.meta_account_id ?? <span className="text-muted">no ad account</span>}
                    </div>
                  </div>
                  {clientActions(c)}
                </div>
                {c.services.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1 text-[10px] text-muted">
                    {c.services.map((s) => (
                      <span key={s} className="rounded bg-surface-3 px-1.5 py-0.5">
                        {s}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Desktop: table. */}
          <div className="card hidden overflow-hidden md:block">
            <TableScroll>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-hairline text-left text-xs uppercase tracking-wide text-muted">
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Ad account</th>
                    <th className="px-4 py-3 font-medium">Services</th>
                    <th className="px-4 py-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data!.clients.map((c) => (
                    <tr key={c.id} className="border-b border-hairline/60 last:border-0 hover:bg-surface-2">
                      <td className="px-4 py-3">
                        <Link to={`/clients/${c.id}`} className="font-medium text-ink hover:text-brand">
                          {c.name}
                        </Link>
                        {c.archived_at && (
                          <span className="ml-2 rounded bg-surface-3 px-1.5 py-0.5 text-[10px] text-muted">
                            archived
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 tnum text-ink-secondary">
                        {c.meta_account_id ?? <span className="text-muted">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1 text-[10px] text-muted">
                          {c.services.length ? (
                            c.services.map((s) => (
                              <span key={s} className="rounded bg-surface-3 px-1.5 py-0.5">
                                {s}
                              </span>
                            ))
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end">{clientActions(c)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          </div>
        </>
      )}

      {editing && (
        <ClientFormModal
          client={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ["clients"] });
          }}
        />
      )}
      {inviteFor && <InviteModal client={inviteFor} onClose={() => setInviteFor(null)} />}
    </div>
  );
}

function ClientFormModal({
  client,
  onClose,
  onSaved,
}: {
  client: Client | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(client?.name ?? "");
  const [metaAccountId, setMetaAccountId] = useState(client?.meta_account_id ?? "");
  const [services, setServices] = useState<string[]>(client?.services ?? []);
  const [notes, setNotes] = useState(client?.notes ?? "");
  const [spendCurrency, setSpendCurrency] = useState(client?.spend_currency ?? "");
  const [profitMargin, setProfitMargin] = useState(
    client?.profit_margin != null ? String(Math.round(client.profit_margin * 100)) : ""
  );
  const [err, setErr] = useState<string | null>(null);

  const accounts = useQuery({
    queryKey: ["adAccounts"],
    queryFn: () => api.get<{ accounts: AdAccount[] }>("/api/meta/ad-accounts"),
    retry: false,
  });

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name,
        meta_account_id: metaAccountId || null,
        services,
        notes: notes || null,
        spend_currency: spendCurrency || null,
        profit_margin: profitMargin.trim() === "" ? null : Number(profitMargin) / 100,
      };
      return client
        ? api.patch(`/api/clients/${client.id}`, payload)
        : api.post("/api/clients", payload);
    },
    onSuccess: onSaved,
    onError: (e) => setErr((e as Error).message),
  });

  const options = accounts.data?.accounts?.length ? accounts.data.accounts : KNOWN_AD_ACCOUNTS;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    save.mutate();
  }

  return (
    <Modal open onClose={onClose} title={client ? "Edit client" : "New client"}>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="label">Name</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>

        <div>
          <label className="label">Meta ad account (optional)</label>
          <select
            className="input mb-2"
            value={options.some((o) => o.id === metaAccountId) ? metaAccountId : ""}
            onChange={(e) => setMetaAccountId(e.target.value)}
          >
            <option value="">— none / enter manually —</option>
            {options.map((a) => (
              <option key={a.name} value={a.id}>
                {a.name} {a.id ? `(${a.id})` : "(id unknown)"}
              </option>
            ))}
          </select>
          <input
            className="input tnum"
            placeholder="act_1234567890"
            value={metaAccountId}
            onChange={(e) => setMetaAccountId(e.target.value)}
          />
          {accounts.isError && (
            <p className="mt-1 text-xs text-muted">
              Live account list unavailable (Meta token not configured) — pick from the known list or
              enter the id manually.
            </p>
          )}
        </div>

        <div>
          <label className="label">Services</label>
          <div className="flex flex-wrap gap-3">
            {SERVICE_OPTIONS.map((s) => (
              <label key={s} className="flex items-center gap-1.5 text-sm text-ink-secondary">
                <input
                  type="checkbox"
                  checked={services.includes(s)}
                  onChange={(e) =>
                    setServices((prev) =>
                      e.target.checked ? [...prev, s] : prev.filter((x) => x !== s)
                    )
                  }
                />
                {s}
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="label">Spend currency</label>
          <select
            className="input max-w-[12rem]"
            value={spendCurrency}
            onChange={(e) => setSpendCurrency(e.target.value)}
          >
            <option value="">Auto-detect on sync</option>
            {CURRENCY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-muted">
            The ad account's native currency for spend (usually set automatically on sync). ROAS
            converts it to MUR using the rates in Settings.
          </p>
        </div>

        <div>
          <label className="label">Profit margin (optional)</label>
          <div className="relative max-w-[12rem]">
            <input
              className="input tnum pr-8"
              type="number"
              min={0}
              max={100}
              step={1}
              placeholder="e.g. 40"
              value={profitMargin}
              onChange={(e) => setProfitMargin(e.target.value)}
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted">
              %
            </span>
          </div>
          <p className="mt-1 text-xs text-muted">
            Gross margin on revenue. Drives the <span className="text-ink-secondary">Profit ROAS</span>{" "}
            line — (revenue × margin) ÷ spend, break-even at 1×. Leave blank to show only gross ROAS.
          </p>
        </div>

        <div>
          <label className="label">Notes</label>
          <textarea className="input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>

        {err && <ErrorNote message={err} />}

        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            {save.isPending && <Spinner className="h-4 w-4" />}
            {client ? "Save changes" : "Create client"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function InviteModal({ client, onClose }: { client: Client; onClose: () => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const logins = useQuery({
    queryKey: ["logins", client.id],
    queryFn: () =>
      api.get<{ logins: { id: string; email: string | null; role: string }[] }>(
        `/api/clients/${client.id}/logins`
      ),
  });

  const invite = useMutation({
    mutationFn: () =>
      api.post<{ user: { email: string }; tempPasswordSet: boolean }>(
        `/api/clients/${client.id}/invite`,
        { email, password: password || undefined }
      ),
    onSuccess: (res) => {
      setErr(null);
      setMsg(
        res.tempPasswordSet
          ? `Created ${res.user.email}. A random password was set — send them a reset link, or re-invite with a chosen password.`
          : `Created ${res.user.email} with the password you set.`
      );
      setEmail("");
      setPassword("");
      qc.invalidateQueries({ queryKey: ["logins", client.id] });
    },
    onError: (e) => setErr((e as Error).message),
  });

  return (
    <Modal open onClose={onClose} title={`Logins — ${client.name}`}>
      <div className="space-y-4">
        <div>
          <div className="label">Existing logins</div>
          {logins.isLoading ? (
            <Spinner />
          ) : logins.data?.logins.length ? (
            <ul className="space-y-1 text-sm">
              {logins.data.logins.map((l) => (
                <li key={l.id} className="flex items-center justify-between rounded bg-surface-2 px-3 py-1.5">
                  <span className="text-ink-secondary">{l.email ?? l.id}</span>
                  <span className="text-[10px] uppercase text-muted">{l.role}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted">No client logins yet.</p>
          )}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            invite.mutate();
          }}
          className="space-y-3 border-t border-hairline pt-4"
        >
          <div className="label">Invite a client login</div>
          <input
            className="input"
            type="email"
            placeholder="client@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            className="input"
            type="text"
            placeholder="Temp password (optional, min 8 chars)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {err && <ErrorNote message={err} />}
          {msg && <p className="text-xs text-good">{msg}</p>}
          <div className="flex justify-end">
            <button type="submit" className="btn-primary" disabled={invite.isPending}>
              {invite.isPending && <Spinner className="h-4 w-4" />}
              Create login
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
